import {gql} from '@apollo/client';
import assert from 'assert';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

import {
  TestOrdersContainerQuery,
  TestOrdersContainerQueryVariables,
} from './__generated/TestOrdersContainerQuery';
import {TEST_ORDERS_VIEW_TABLE_TEST_ORDER_FRAGMENT, TestOrdersViewTable} from './TestOrdersViewTable';

export const TEST_ORDERS_CONTAINER_QUERY = gql`
  query TestOrdersContainerQuery($containerId: StrictString!) @skipAudit @engComponent(name: SAMPLE_TESTS) {
    container(id: $containerId) {
      ... on ContainerNode {
        id
        ...StudyableTypedValueDataGraphQL
        testOrders {
          ...TestOrdersViewTableTestOrderFragment
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${TEST_ORDERS_VIEW_TABLE_TEST_ORDER_FRAGMENT}
`;

interface Props {
  containerId: string;
}

export default function ContainerTestOrdersView(props: Props): JSX.Element {
  assert(APIUtil.isContainer(props.containerId), 'Expected a container ID');
  return (
    <div
      className="react-tab-container"
      {...getTestProps({component: 'ContainerTestOrdersView', element: 'root'})}
    >
      <BenchlingQuery<TestOrdersContainerQuery, TestOrdersContainerQueryVariables>
        query={TEST_ORDERS_CONTAINER_QUERY}
        variables={{containerId: props.containerId}}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {(data) => {
          if (isInaccessible(data.container)) {
            // If this is a read-only share link, the full item will be inaccessible and we don't want to
            // render a test orders table at all
            return null;
          }
          const studyIds = accessibleObjs(data.container.studies).map((study) => study.id);

          const testOrders = accessibleObjs(data.container.testOrders);

          return (
            <TestOrdersViewTable
              testOrders={testOrders}
              itemId={props.containerId}
              studyIds={studyIds}
              allowCreateTestOrders={false}
            />
          );
        }}
      </BenchlingQuery>
    </div>
  );
}
